import { FilterBar, FilterBarField } from "design-system/molecules/FilterBar"
import { cn } from "design-system/utils"
import { ensure } from "lopermut"
import { useCampaignsQuery } from "modules/campaigns/hooks/useCampaignsQuery"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Route as WhatsappTemplatesRoute } from "routes/_base/_auth/settings/channels/whatsapp/templates"
import { WhatsappTemplatesCampaignFilter, WhatsappTemplatesStatusFilter } from "schema"

type Props = {
  trailing: ReactNode
}

type Search = ReturnType<typeof WhatsappTemplatesRoute.useSearch>
type WhatsappTemplatesFilterBy = NonNullable<Search["filterBy"]>

export function WhatsappTemplatesFilter({ trailing }: Props) {
  const { t } = useTranslation()
  const search = WhatsappTemplatesRoute.useSearch()
  const navigate = WhatsappTemplatesRoute.useNavigate()
  const labels = useFilterBarLabels()

  const selectedCampaignId =
    search.filterBy?.campaignId === WhatsappTemplatesCampaignFilter.Unassigned
      ? undefined
      : search.filterBy?.campaignId

  const { data: campaigns = [] } = useCampaignsQuery({
    query: {
      filterBy: { archived: false },
      includeIds: selectedCampaignId ? [selectedCampaignId] : undefined,
      total: 100,
      sortBy: { field: "name", order: "asc" },
    },
  })

  function onChangeFilter(changes: Partial<WhatsappTemplatesFilterBy>) {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        filterBy: { ...previous.filterBy, ...changes },
      }),
    })
  }

  function onClearFilters() {
    navigate({
      search: (previous) => ({ ...previous, page: 1, filterBy: undefined, query: undefined }),
    })
  }

  const isOneTime = search.filterBy?.isOneTime

  const fields: FilterBarField[] = [
    {
      key: "isOneTime",
      type: "select",
      label: t("campaigns.templates.whatsapp.filters.templateType"),
      value: isOneTime === undefined ? undefined : String(isOneTime),
      onChange: (value) =>
        onChangeFilter({
          isOneTime: value === undefined || value === "all" ? value : value === "true",
        }),
      options: [
        { value: "false", label: t("campaigns.templates.whatsapp.filters.normalTemplates") },
        { value: "true", label: t("campaigns.templates.whatsapp.filters.oneTimeTemplates") },
        { value: "all", label: t("campaigns.templates.whatsapp.filters.allTemplates") },
      ],
    },
    {
      key: "status",
      type: "select",
      label: t("campaigns.templates.whatsapp.filters.status"),
      value: search.filterBy?.status,
      onChange: (value) =>
        onChangeFilter({
          status: WhatsappTemplatesStatusFilter.find((status) => status === value),
        }),
      options: WhatsappTemplatesStatusFilter.map((status) => ({
        value: status,
        label: t(`campaigns.templates.whatsapp.status.${status}`),
      })),
    },
    {
      key: "campaignId",
      type: "select",
      label: t("campaigns.templates.whatsapp.filters.campaign"),
      value: search.filterBy?.campaignId,
      onChange: (value) => onChangeFilter({ campaignId: value }),
      options: [
        {
          value: WhatsappTemplatesCampaignFilter.Unassigned,
          label: t("campaigns.templates.whatsapp.filters.campaignUnassigned"),
        },
        ...campaigns.map((campaign) => {
          const hasName = campaign.name && campaign.name.length > 0

          return {
            value: campaign.id,
            label: hasName ? ensure(campaign.name) : t("campaigns.untitled"),
            className: cn(!hasName && "text-muted-foreground"),
          }
        }),
      ],
    },
  ]

  return (
    <FilterBar
      className="w-full"
      fields={fields}
      labels={labels}
      onClearFilters={onClearFilters}
      searchQuery={search.query}
      onSearchQueryChange={(query) =>
        navigate({ search: (previous) => ({ ...previous, query: query || undefined, page: 1 }) })
      }
      searchPlaceholder={t("campaigns.templates.search")}
      trailing={trailing}
    />
  )
}
